import { MouseEvent, ReactNode, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  DocumentThumbnail,
  DocumentViewer,
  MiChip,
  MiMenu,
} from "@miplatform/components";
import { getCurrencyString, toUpperCase } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";

export const MaterialCard = ({
  item = {},
  menuData = [],
  leftAction,
  rightAction,
  onClickCard,
  isSelected = false,
  fieldNote = "",
  customBgColor,
  isSuppliedByBuilder = false,
  isLoading = false,
  noCardStyle = false,
  isRemoved = false,
  renderCustomDetails = null,
  showPrice = false,
}: MaterialCardProps) => {
  const { classes } = useStyles({
    isSelected,
    onClickCard,
    customBgColor,
    noCardStyle,
    isRemoved,
  });
  const [imagesInViewer, setImagesInViewer] = useState<MaterialDocument[]>([]);
  const [showImagesViewer, setShowImagesViewer] = useState(false);

  const handleDocumentClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (item.document) {
      setImagesInViewer([item.document]);
      setShowImagesViewer(true);
    }
  };

  const getItemLabel = () => {
    const partNumber = item.manufacturerPartNumber ?? "";
    const desc = item.category?.itemDescription ?? item.itemDescription ?? "";
    return `${toUpperCase(partNumber)} | ${toUpperCase(desc)}`;
  };

  return (
    <Box className={classes.card} onClick={onClickCard}>
      <Box className={classes.cardContent}>
        <Box>{leftAction}</Box>
        <Box className={classes.attributeTextContainer}>
          <Typography className={classes.itemCodeText} variant="subtitle2">
            {getItemLabel()}
          </Typography>
          <Box className={classes.attributeList}>
            {item.category?.categoryName && (
              <MiChip
                label={toUpperCase(item.category.categoryName)}
                size="small"
                className={classes.chip}
              />
            )}
            {item.attributes
              ?.filter((a) => a.value)
              .map((attr, i, arr) => (
                <Typography
                  key={`${attr.attributeId} ${attr.value}`}
                  variant="caption"
                  className={classes.attributeText}
                >
                  {toUpperCase(attr.value)} {i < arr.length - 1 && " |"}
                </Typography>
              ))}
          </Box>
          {fieldNote && (
            <Box className={classes.fieldNoteContainer}>
              <Typography variant="subtitle2" fontWeight="bold">
                {`Field Note:\u00A0`}
              </Typography>
              <Typography variant="subtitle2">{fieldNote}</Typography>
            </Box>
          )}
        </Box>
        {renderCustomDetails}
        <Box className={classes.rightActionContainer}>
          {isSuppliedByBuilder && (
            <Box className={classes.unitCostContainer}>
              <Typography className={classes.unitCostLabel} variant="subtitle2">
                {`BUILDER\nSUPPLIED`}
              </Typography>
            </Box>
          )}
          {showPrice && (
            <Box className={classes.unitCostContainer}>
              <Typography className={classes.unitCostLabel} variant="subtitle2">
                COST
              </Typography>
              <Typography
                variant="caption"
                className={`${classes.unitCost} ${
                  isSuppliedByBuilder ? classes.strikeThrough : ""
                }`}
              >
                {getCurrencyString(item.manufacturerListPrice)}
              </Typography>
            </Box>
          )}
          <DocumentThumbnail
            document={item.document}
            onClick={handleDocumentClick}
            documentCount={1}
          />
          {rightAction}
          {menuData.length > 0 && (
            <MiMenu
              data={menuData}
              showHover
              testID={`material-card-${item.id}`}
              disabled={isLoading}
            />
          )}
        </Box>
      </Box>
      <DocumentViewer
        documents={imagesInViewer}
        onUnfullscreen={() => setShowImagesViewer(false)}
        fullScreenStatus={showImagesViewer}
      />
    </Box>
  );
};

const useStyles = makeStyles<MaterialCardStyleProps>()(
  (
    theme,
    { isSelected, onClickCard, customBgColor, noCardStyle, isRemoved }
  ) => ({
    card: noCardStyle
      ? {
          backgroundColor: isSelected
            ? theme.palette.primary.light
            : customBgColor || theme.palette.common.white,
          cursor: onClickCard && "pointer",
        }
      : {
          marginTop: theme.spacing(2),
          width: "100%",
          border: `1px solid ${theme.palette.grey.accent}`,
          borderRadius: 4,
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          cursor: onClickCard && "pointer",
          boxShadow: `0px 2px 2px ${theme.palette.grey.accent}`,
          backgroundColor: isSelected
            ? theme.palette.primary.light
            : customBgColor || theme.palette.common.white,
        },
    cardContent: {
      display: "flex",
      width: "100%",
      padding: theme.spacing(1),
      gap: theme.spacing(1),
      alignItems: "center",
    },
    attributeTextContainer: {
      width: "100%",
    },
    attributeList: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    attributeText: {
      color: theme.palette.text.secondary,
    },
    itemCodeText: {
      color: isRemoved ? theme.palette.red.dark : theme.palette.primary.main,
      fontWeight: theme.fontWeight.semiBold,
      textDecoration: isRemoved ? "line-through" : "none",
    },
    chip: {
      borderRadius: 2,
      height: 16,
      backgroundColor: theme.palette.grey.light,
      border: `1px solid ${theme.palette.grey.accent}`,
      "& .MuiChip-label": {
        padding: theme.spacing(0, 1),
      },
    },
    rightActionContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      paddingLeft: theme.spacing(1),
      marginLeft: theme.spacing(1),
    },
    unitCostContainer: {
      height: 40,
      padding: theme.spacing(0, 4),
      borderRadius: 4,
      color: theme.palette.secondary.main,
      backgroundColor: theme.palette.secondary.light,
      whiteSpace: "nowrap",
      display: "flex",
      flexDirection: "column",
    },
    unitCost: {
      textAlign: "center",
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.bold,
    },
    unitCostLabel: {
      top: theme.spacing(1),
      position: "relative",
      textAlign: "center",
      whiteSpace: "pre-wrap",
      fontSize: theme.fontSize.xsmall,
      fontWeight: theme.fontWeight.semiBold,
    },
    strikeThrough: {
      textDecoration: "line-through",
      color: theme.palette.red.dark,
    },
    fieldNoteContainer: {
      display: "flex",
      alignItems: "center",
    },
  })
);

interface MaterialAttribute {
  id?: string | number;
  attributeId?: string | number;
  attributeName: string;
  value: string;
}

interface MaterialCategory {
  categoryName?: string;
  itemDescription?: string;
}

interface MaterialDocument {
  createdOn: string;
  referenceId: string;
  contentType: string;
  filename: string;
  title: string;
  modifiedOn: string;
  id: string;
  metadata: {
    title: string;
  };
  tempURL: string;
  uri: string;
}

interface MaterialItem {
  id?: string | number;
  manufacturerPartNumber?: string;
  manufacturerListPrice?: number | null;
  itemDescription?: string;
  category?: MaterialCategory;
  attributes?: MaterialAttribute[];
  document?: MaterialDocument;
}

interface MenuItem {
  label?: string;
  icon?: string;
  color?: string;
  action?: () => void;
}

interface MaterialCardProps {
  item?: MaterialItem;
  menuData?: MenuItem[];
  leftAction?: ReactNode;
  rightAction?: ReactNode;
  onClickCard?: (e: MouseEvent<HTMLDivElement>) => void;
  isSelected?: boolean;
  fieldNote?: string;
  customBgColor?: string;
  isSuppliedByBuilder?: boolean;
  isLoading?: boolean;
  noCardStyle?: boolean;
  isRemoved?: boolean;
  renderCustomDetails?: ReactNode;
  showPrice?: boolean;
}

interface MaterialCardStyleProps {
  isSelected: boolean;
  onClickCard?: (e: MouseEvent<HTMLDivElement>) => void;
  customBgColor?: string;
  noCardStyle: boolean;
  isRemoved: boolean;
}
